import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { PropertyPermission } from 'src/modules/shared/types/permissions.type';
import type { User } from 'src/api/generated';
import React, { useMemo, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import { PiFunnelX } from 'react-icons/pi';
import { NumberRangeFilter } from 'src/modules/shared/components/molecules/inputs/number-range-filter';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import SearchableSelect, {
  type SearchableSelectItem,
} from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { type PropertyFiltersState } from '../../types/property-filters.types';
import type {
  LocationRegionOption,
  LocationLocalityOption,
} from 'src/modules/shared/types/location.types';
import {
  useLocalities,
  useRegions,
} from 'src/modules/shared/hooks/api/location/location';
import { useNumberRangeFilter } from 'src/modules/shared/hooks/filters/number-range-filter';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';

const SEARCH_DEBOUNCE_MS = 300;

interface PropertyFiltersProps {
  filters: PropertyFiltersState;
  onChange: (filters: PropertyFiltersState) => void;
  onClear: () => void;
  users: User[];
  usersLoading: boolean;
  className?: string;
  showSearch?: boolean;
  layout?: 'responsive' | 'stacked';
}

export const PropertyFilters: React.FC<PropertyFiltersProps> = ({
  filters,
  onChange,
  onClear,
  users,
  usersLoading,
  className = '',
  showSearch = true,
  layout = 'responsive',
}) => {
  const { t } = useI18n();
  const { hasPermission: canReadAll } = useHasPermissions(
    PropertyPermission.Read
  );
  const { regions, loading: regionsLoading } = useRegions();
  const { localities, loading: localitiesLoading } = useLocalities();
  const [localSearchValue, setLocalSearchValue] = useState(
    filters.search ?? ''
  );
  const debouncedSearchValue = useDebounce(
    localSearchValue,
    SEARCH_DEBOUNCE_MS
  );

  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);
  };

  React.useEffect(() => {
    onChange({ ...filters, search: debouncedSearchValue });
  }, [debouncedSearchValue, filters, onChange]);

  const salePriceRange = useNumberRangeFilter({
    applied: {
      from: filters.salePriceMin,
      to: filters.salePriceMax,
    },
    onApply: (value) =>
      onChange({
        ...filters,
        salePriceMin: value.from,
        salePriceMax: value.to,
      }),
  });

  const rentPriceRange = useNumberRangeFilter({
    applied: {
      from: filters.rentPriceMin,
      to: filters.rentPriceMax,
    },
    onApply: (value) =>
      onChange({
        ...filters,
        rentPriceMin: value.from,
        rentPriceMax: value.to,
      }),
  });

  const operationOptions = useMemo(
    () => [
      {
        id: 'ALL',
        content: t('catalog.properties.filters.operationAll'),
        selected: filters.operation === 'ALL',
      },
      {
        id: 'SALE',
        content: t('catalog.properties.filters.operationSale'),
        selected: filters.operation === 'SALE',
      },
      {
        id: 'RENT',
        content: t('catalog.properties.filters.operationRent'),
        selected: filters.operation === 'RENT',
      },
      {
        id: 'TEMPORARY_RENT',
        content: t('catalog.properties.filters.operationTemporaryRent'),
        selected: filters.operation === 'TEMPORARY_RENT',
      },
    ],
    [filters.operation, t]
  );

  const selectedOperationLabel =
    operationOptions.find((option) => option.id === filters.operation)
      ?.content ?? t('catalog.properties.filters.operationAll');

  const handleOperationChange = (option: (typeof operationOptions)[0]) => {
    onChange({
      ...filters,
      operation: option.id as PropertyFiltersState['operation'],
    });
  };

  const activeStatusOptions = useMemo(
    () => [
      {
        id: 'ALL',
        content: t('catalog.properties.filters.activeStatusAll'),
        selected: filters.activeStatus === 'ALL',
      },
      {
        id: 'ACTIVE',
        content: t('catalog.properties.filters.activeStatusActive'),
        selected: filters.activeStatus === 'ACTIVE',
      },
      {
        id: 'INACTIVE',
        content: t('catalog.properties.filters.activeStatusInactive'),
        selected: filters.activeStatus === 'INACTIVE',
      },
    ],
    [filters.activeStatus, t]
  );

  const selectedActiveStatusLabel =
    activeStatusOptions.find((option) => option.id === filters.activeStatus)
      ?.content ?? t('catalog.properties.filters.activeStatusActive');

  const handleActiveStatusChange = (
    option: (typeof activeStatusOptions)[0]
  ) => {
    onChange({
      ...filters,
      activeStatus: option.id as PropertyFiltersState['activeStatus'],
    });
  };

  const hasFilters =
    (filters.search?.trim().length ?? 0) > 0 ||
    filters.operation !== 'ALL' ||
    filters.activeStatus !== 'ACTIVE' ||
    typeof filters.userId === 'number' ||
    typeof filters.salePriceMin === 'number' ||
    typeof filters.salePriceMax === 'number' ||
    typeof filters.rentPriceMin === 'number' ||
    typeof filters.rentPriceMax === 'number' ||
    (filters.region?.trim().length ?? 0) > 0 ||
    (filters.locality?.trim().length ?? 0) > 0;

  const handleClearClick = () => {
    // Reset local UI state so inputs reflect cleared filters immediately.
    setLocalSearchValue('');
    salePriceRange.resetLocal();
    rentPriceRange.resetLocal();
    onClear();
  };

  const regionOptions: SearchableSelectItem[] = useMemo(() => {
    const regionOptionModels: LocationRegionOption[] = regions.map(
      (regionDto) => ({
        code: regionDto.code,
        label: regionDto.name,
      })
    );

    const baseOptions: SearchableSelectItem[] = [
      {
        value: '',
        content: t('catalog.properties.filters.regionAll'),
      },
    ];

    if (regionOptionModels.length > 0) {
      baseOptions.push({ separator: true });
      baseOptions.push(
        ...regionOptionModels.map((region) => ({
          value: region.code,
          content: region.label,
        }))
      );
    }

    return baseOptions;
  }, [regions, t]);

  const localityOptions: SearchableSelectItem[] = useMemo(() => {
    const options: SearchableSelectItem[] = [
      {
        value: '',
        content: t('catalog.properties.filters.localityAll'),
      },
    ];

    const localityOptionModels: LocationLocalityOption[] = localities.map(
      (localityDto) => ({
        code: localityDto.code,
        label: localityDto.name,
        regionCode: localityDto.regionCode,
      })
    );

    const filteredLocalities =
      filters.region && filters.region.trim().length > 0
        ? localityOptionModels.filter(
            (locality) => locality.regionCode === filters.region
          )
        : localityOptionModels;

    if (filteredLocalities.length === 0) return options;

    return [
      options[0],
      { separator: true },
      ...filteredLocalities.map((locality) => ({
        value: locality.code,
        content: locality.label,
      })),
    ];
  }, [filters.region, localities, t]);

  const assignedUserOptions: SearchableSelectItem[] = useMemo(() => {
    const baseOptions: SearchableSelectItem[] = [
      {
        value: '',
        content: t('catalog.properties.filters.assignedAll'),
      },
      {
        value: '-1',
        content: t('catalog.properties.filters.assignedUnassigned'),
      },
    ];

    if (users.length > 0) {
      baseOptions.push({ separator: true });
      baseOptions.push(
        ...users.map((user) => ({
          value: String(user.id),
          content: formatFullName(user.firstName, user.lastName, user.email),
        }))
      );
    }

    return baseOptions;
  }, [t, users]);

  const isStacked = layout === 'stacked';
  const containerClasses = isStacked
    ? 'mx-auto flex w-full max-w-[340px] flex-col gap-3 p-4'
    : 'flex flex-col gap-3 p-4';
  const topRowClasses = isStacked
    ? 'mx-auto grid w-full max-w-[340px] grid-cols-1 gap-3'
    : 'grid grid-cols-1 gap-3 lg:flex lg:flex-wrap lg:items-end';
  const bottomRowClasses = isStacked
    ? 'mx-auto grid w-full max-w-[340px] grid-cols-1 gap-3'
    : 'grid grid-cols-1 gap-3 lg:flex lg:flex-wrap lg:items-end';
  const controlWidthClasses = isStacked
    ? 'min-w-0 w-full'
    : 'min-w-0 w-full lg:min-w-[180px] lg:max-w-[240px]';
  const activeWidthClasses = isStacked
    ? 'min-w-0 w-full'
    : 'min-w-0 w-full lg:min-w-[140px] lg:max-w-[200px]';
  const rangeWidthClasses = isStacked
    ? 'min-w-0 w-full max-w-none'
    : 'min-w-0 w-full max-w-none lg:min-w-[220px] lg:max-w-[240px]';

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className={topRowClasses}>
        {showSearch ? (
          <div
            className={`flex flex-col gap-1 ${
              isStacked ? 'w-full' : 'min-w-[260px] flex-1'
            }`}
          >
            <LabelTextInput
              label={t('catalog.properties.filters.searchLabel')}
              size="md"
              labelPosition="top"
              inputProps={{
                placeholder: t('catalog.properties.filters.searchPlaceholder'),
                value: localSearchValue,
                onChange: (event) => handleSearchChange(event.target.value),
              }}
            />
          </div>
        ) : null}

        <div className={controlWidthClasses}>
          <MenuDropdown
            label={selectedOperationLabel}
            items={operationOptions.map((option) => ({
              content: option.content,
              selected: option.selected,
              onClick: () => handleOperationChange(option),
            }))}
            appearance="outline"
            variant="secondary"
            size="md"
            className="w-full"
          />
        </div>

        <div className={activeWidthClasses}>
          <MenuDropdown
            label={selectedActiveStatusLabel}
            items={activeStatusOptions.map((option) => ({
              content: option.content,
              selected: option.selected,
              onClick: () => handleActiveStatusChange(option),
            }))}
            appearance="outline"
            variant="secondary"
            size="md"
            className="w-full"
          />
        </div>

        <div className={controlWidthClasses}>
          {canReadAll && (
            <SearchableSelect
              size="md"
              options={assignedUserOptions}
              value={filters.userId !== undefined ? String(filters.userId) : ''}
              loading={usersLoading}
              onChange={(value) => {
                const parsed = value ? Number(value) : undefined;
                onChange({
                  ...filters,
                  userId: Number.isFinite(parsed) ? parsed : undefined,
                });
              }}
              placeholder={t('catalog.properties.filters.assignedAll')}
            />
          )}
        </div>

        <div className={controlWidthClasses}>
          <SearchableSelect
            size="md"
            options={regionOptions}
            value={filters.region ?? ''}
            loading={regionsLoading}
            onChange={(value) =>
              onChange({
                ...filters,
                region: value === '' ? undefined : value,
              })
            }
            placeholder={t('catalog.properties.filters.regionAll')}
          />
        </div>

        <div className={controlWidthClasses}>
          <SearchableSelect
            size="md"
            options={localityOptions}
            value={filters.locality ?? ''}
            loading={regionsLoading || localitiesLoading}
            onChange={(value) =>
              onChange({
                ...filters,
                locality: value === '' ? undefined : value,
              })
            }
            placeholder={t('catalog.properties.filters.localityAll')}
          />
        </div>
      </div>

      <div className={bottomRowClasses}>
        <NumberRangeFilter
          label={t('catalog.properties.filters.salePriceRange')}
          placeholderFrom={t('catalog.properties.filters.rangeFrom')}
          placeholderTo={t('catalog.properties.filters.rangeTo')}
          valueFrom={salePriceRange.local.from}
          valueTo={salePriceRange.local.to}
          size="md"
          className={rangeWidthClasses}
          onChange={salePriceRange.onChange}
          onApply={salePriceRange.apply}
        />

        <NumberRangeFilter
          label={t('catalog.properties.filters.rentPriceRange')}
          placeholderFrom={t('catalog.properties.filters.rangeFrom')}
          placeholderTo={t('catalog.properties.filters.rangeTo')}
          valueFrom={rentPriceRange.local.from}
          valueTo={rentPriceRange.local.to}
          size="md"
          className={rangeWidthClasses}
          onChange={rentPriceRange.onChange}
          onApply={rentPriceRange.apply}
        />

        {hasFilters && (
          <Button
            variant="primary"
            appearance="soft"
            size="md"
            onClick={handleClearClick}
            className={`${isStacked ? 'w-full justify-center' : ''} flex items-center gap-2`}
          >
            <PiFunnelX className="w-4 h-4" />
            <Text size="small">{t('catalog.properties.filters.clear')}</Text>
          </Button>
        )}
      </div>
    </div>
  );
};
